import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Upload, LayoutDashboard, Bot, Database, TrendingUp, BarChart3, GitCompare, Layers, Calculator, LogOut, User, Activity, Zap, Gauge, Receipt, CalendarClock, Scale, DollarSign, FileText, Moon, Sun, Radio, Globe } from 'lucide-react';
import MarketDataStatus from './MarketDataStatus';

// Custom Unicorn icon component
const UnicornIcon = ({ className }) => (
  <span className={className} style={{ fontSize: '16px', lineHeight: 1 }}>🦄</span>
);

// Custom Goose icon component
const GooseIcon = ({ className }) => (
  <span className={className} style={{ fontSize: '16px', lineHeight: 1 }}>🪿</span>
);

const Navigation = ({ username, onLogout, isAdmin = false, darkMode = false, onToggleDarkMode }) => {
  const location = useLocation();
  const isPathActive = (itemPath) => location.pathname === itemPath || location.pathname.startsWith(`${itemPath}/`);

  // Define all navigation items with adminOnly flag
  const allNavItems = [
    {
      path: '/',
      label: 'Dashboard',
      icon: LayoutDashboard,
      description: 'Trading dashboard for yesterday through day after tomorrow',
      group: 'main',
      adminOnly: true
    },
    {
      path: '/upload',
      label: 'Upload',
      icon: Upload,
      description: 'Upload energy data files',
      group: 'main',
      adminOnly: true
    },
    {
      path: '/predictions',
      label: 'Predictions',
      icon: Bot,
      description: 'View model predictions',
      group: 'main',
      adminOnly: false
    },
    {
      path: '/live-preds',
      label: 'Live Preds',
      icon: Radio,
      description: 'Intraday prediction runs, refreshed every few minutes',
      group: 'main',
      adminOnly: true
    },
    {
      path: '/data',
      label: 'Data',
      icon: Database,
      description: 'View historical data',
      group: 'main',
      adminOnly: true
    },
    {
      path: '/base-model',
      label: 'Base Model',
      icon: TrendingUp,
      description: 'Run backtesting analysis',
      group: 'analysis',
      adminOnly: true,
      hidden: true
    },
    {
      path: '/goose-model',
      label: 'Goose Model',
      icon: GooseIcon,
      description: 'Goose model with collapse-risk-aware shifts (risk_score)',
      group: 'analysis',
      adminOnly: true
    },
    {
      path: '/goose-ida1',
      label: 'Goose IDA1',
      icon: GooseIcon,
      description: 'IDA1 + collapse-risk-aware shifts (risk_score)',
      group: 'analysis',
      adminOnly: true
    },
    {
      path: '/goose-order-builder',
      label: 'Goose Order Builder',
      icon: GooseIcon,
      description: 'Goose Fix I orders + collapse-risk shift (risk_score)',
      group: 'analysis',
      adminOnly: true
    },
    {
      path: '/goose-schedule',
      label: 'Goose Schedule',
      icon: GooseIcon,
      description: 'Goose schedule and positions',
      group: 'analysis',
      adminOnly: true
    },
    {
      path: '/diagnosis',
      label: 'Diagnosis',
      icon: Activity,
      description: 'Forecast errors, direction and uncertainty against Fix II',
      group: 'main',
      adminOnly: true
    },
    {
      path: '/daily-analysis',
      label: 'Daily Analysis',
      icon: BarChart3,
      description: 'Compare predictions vs actual',
      group: 'main',
      adminOnly: true,
      hidden: true
    },
    {
      path: '/prices',
      label: 'Prices',
      icon: DollarSign,
      description: 'View market prices across all auctions',
      group: 'main',
      adminOnly: true
    },
    {
      path: '/europe-map',
      label: 'Europe Map',
      icon: Globe,
      description: 'Day-ahead prices and scheduled exchanges across European bidding zones',
      group: 'main',
      adminOnly: true
    },
    {
      path: '/logs',
      label: 'Logs',
      icon: FileText,
      description: 'View application log files',
      group: 'main',
      adminOnly: true
    },
    {
      path: '/combined-models',
      label: 'Combined Models',
      icon: Layers,
      description: 'Combined analysis of models',
      group: 'analysis',
      adminOnly: true,
      hidden: true
    },
    {
      path: '/combined-step-models',
      label: 'Step Models',
      icon: Activity,
      description: 'Step-based analysis of models',
      group: 'analysis',
      adminOnly: true,
      hidden: true
    },
    {
      path: '/probabilistic-model',
      label: 'Probabilistic Model',
      icon: Zap,
      description: 'Probabilistic forecasts with confidence intervals',
      group: 'probabilistic',
      adminOnly: true,
      hidden: true
    },
    {
      path: '/prob-step-model',
      label: 'Probabilistic Step Model',
      icon: Gauge,
      description: 'Step-based analysis with Probabilistic Model',
      group: 'probabilistic',
      adminOnly: true,
      hidden: true
    },
    {
      path: '/probabilistic-order-builder',
      label: 'Probabilistic Order Builder',
      icon: Receipt,
      description: 'Build orders using probabilistic predictions',
      group: 'probabilistic',
      adminOnly: true,
      hidden: true
    },
    {
      path: '/schedule',
      label: 'Schedule',
      icon: CalendarClock,
      description: 'Upload Noble transactions and generate schedule',
      group: 'probabilistic',
      adminOnly: true,
      hidden: true
    },
    {
      path: '/order-builder',
      label: 'Order Builder',
      icon: Calculator,
      description: 'Build trading orders',
      group: 'analysis',
      adminOnly: false,
      hidden: true
    },
    {
      path: '/market-influence',
      label: 'Market Influence',
      icon: Scale,
      description: 'View market influence analysis',
      group: 'results',
      adminOnly: true
    },
    {
      path: '/results',
      label: 'Results',
      icon: UnicornIcon,
      description: 'View trading results',
      group: 'results',
      adminOnly: true
    },
    {
      path: '/fix2-v5-sim/lets-trade',
      label: 'Goose IDA2',
      icon: GooseIcon,
      description: 'Goose live simulation with IDA2 overlay',
      group: 'analysis',
      adminOnly: true
    }
  ];

  // Filter items based on admin status, and skip items explicitly hidden
  const navItems = allNavItems.filter(item => (isAdmin || !item.adminOnly) && !item.hidden);

  // For regular users, show all items in one row; for admin, split into three rows
  const mainItems = isAdmin
    ? navItems.filter(item => item.group === 'main')
    : navItems; // All items in main row for regular users
  const analysisItems = isAdmin
    ? navItems.filter(item => item.group === 'analysis')
    : []; // No second row for regular users
  const probabilisticItems = isAdmin
    ? navItems.filter(item => item.group === 'probabilistic')
    : []; // No third row for regular users
  const ida1Items = isAdmin
    ? navItems.filter(item => item.group === 'ida1')
    : []; // No ida1 row for regular users
  const resultsItems = isAdmin
    ? navItems.filter(item => item.group === 'results')
    : []; // No fourth row for regular users

  return (
    <nav className={`${darkMode ? 'bg-slate-900 shadow-xl shadow-slate-950/30 border-b border-slate-800' : 'bg-white shadow-md'}`}>
      {/* Main navigation row */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex min-h-16 items-center justify-between gap-4 py-2 sm:py-0">
          <div className="flex min-w-0 items-center">
            {/* Logo/Brand */}
            <div className="flex-shrink-0 flex items-center">
              <h1 className={`text-xl font-bold ${darkMode ? 'text-slate-100' : 'text-gray-800'}`}>Puzzle Trading</h1>
            </div>
          </div>

          {/* User info and logout */}
          <div className="hidden sm:flex sm:items-center sm:space-x-3">
            <MarketDataStatus darkMode={darkMode} />
            <button
              type="button"
              onClick={onToggleDarkMode}
              className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                darkMode
                  ? 'border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700 focus:ring-slate-500 focus:ring-offset-slate-900'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-gray-400'
              }`}
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <div className={`flex items-center text-sm ${darkMode ? 'text-slate-200' : 'text-gray-700'}`}>
              <User className="w-4 h-4 mr-1" />
              <span>{username}</span>
            </div>
            <button
              onClick={onLogout}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              <LogOut className="w-4 h-4 mr-1" />
              Logout
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center sm:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-500"
              onClick={() => {
                // Toggle mobile menu
                const mobileMenu = document.getElementById('mobile-menu');
                mobileMenu.classList.toggle('hidden');
              }}
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Primary navigation row - split from account controls so PL does not overlap */}
      <div className={`hidden sm:block border-t ${darkMode ? 'border-slate-800' : 'border-gray-200'}`}>
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-12 flex-wrap items-center gap-x-6 gap-y-1 py-1 xl:gap-x-8">
            {mainItems.map((item) => {
              const Icon = item.icon;
              const isActive = isPathActive(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`inline-flex h-10 shrink-0 items-center whitespace-nowrap border-b-2 px-1 text-sm font-medium transition-colors ${
                    isActive
                      ? (darkMode ? 'border-slate-200 text-slate-100' : 'border-gray-900 text-gray-900')
                      : (darkMode ? 'border-transparent text-slate-400 hover:border-slate-600 hover:text-slate-200' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700')
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Probabilistic navigation row - only show for admin */}
      {probabilisticItems.length > 0 && (
        <div className={`hidden sm:block border-t ${darkMode ? 'bg-[#1a1430] border-slate-800' : 'bg-purple-50 border-gray-200'}`}>
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-8 h-12">
              {probabilisticItems.map((item) => {
                const Icon = item.icon;
                const isActive = isPathActive(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`inline-flex items-center px-1 text-sm font-medium transition-colors ${
                      isActive
                        ? (darkMode ? 'text-fuchsia-200 border-b-2 border-fuchsia-400' : 'text-purple-900 border-b-2 border-purple-900')
                        : (darkMode ? 'text-fuchsia-300 hover:text-fuchsia-200' : 'text-purple-600 hover:text-purple-800')
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* IDA1 navigation row - only show for admin */}
      {ida1Items.length > 0 && (
        <div className={`hidden sm:block border-t ${darkMode ? 'bg-[#10262a] border-slate-800' : 'bg-teal-50 border-gray-200'}`}>
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-8 h-12">
              {ida1Items.map((item) => {
                const Icon = item.icon;
                const isActive = isPathActive(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`inline-flex items-center px-1 text-sm font-medium transition-colors ${
                      isActive
                        ? (darkMode ? 'text-teal-200 border-b-2 border-teal-400' : 'text-teal-900 border-b-2 border-teal-900')
                        : (darkMode ? 'text-teal-300 hover:text-teal-200' : 'text-teal-600 hover:text-teal-800')
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Models navigation row - only show for admin */}
      {analysisItems.length > 0 && (
        <div className={`hidden sm:block border-t ${darkMode ? 'bg-[#151c2e] border-slate-800' : 'bg-gray-50 border-gray-200'}`}>
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-8 h-12">
              {analysisItems.map((item) => {
                const Icon = item.icon;
                const isActive = isPathActive(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`inline-flex items-center px-1 text-sm font-medium transition-colors ${
                      isActive
                        ? (darkMode ? 'text-slate-100 border-b-2 border-slate-200' : 'text-gray-900 border-b-2 border-gray-900')
                        : (darkMode ? 'text-slate-300 hover:text-slate-100' : 'text-gray-600 hover:text-gray-800')
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Results navigation row - only show for admin */}
      {resultsItems.length > 0 && (
        <div className={`hidden sm:block border-t ${darkMode ? 'bg-[#2a1322] border-slate-800' : 'bg-pink-50 border-gray-200'}`}>
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-8 h-12">
              {resultsItems.map((item) => {
                const Icon = item.icon;
                const isActive = isPathActive(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`inline-flex items-center px-1 text-sm font-medium transition-colors ${
                      isActive
                        ? (darkMode ? 'text-pink-200 border-b-2 border-pink-400' : 'text-pink-900 border-b-2 border-pink-900')
                        : (darkMode ? 'text-pink-300 hover:text-pink-200' : 'text-pink-600 hover:text-pink-800')
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Mobile menu */}
      <div className={`sm:hidden hidden ${darkMode ? 'bg-slate-900 border-t border-slate-800' : ''}`} id="mobile-menu">
        <div className="pt-2 pb-3 space-y-1">
          {/* Main Nav Items */}
          {mainItems.map((item) => {
            const Icon = item.icon;
            const isActive = isPathActive(item.path);
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors ${
                  isActive
                    ? (darkMode ? 'bg-slate-800 border-slate-200 text-slate-100' : 'bg-gray-50 border-gray-900 text-gray-900')
                    : (darkMode ? 'border-transparent text-slate-400 hover:bg-slate-800 hover:border-slate-600 hover:text-slate-200' : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700')
                }`}
                onClick={() => {
                  document.getElementById('mobile-menu').classList.add('hidden');
                }}
              >
                <div className="flex items-center">
                  <Icon className="w-5 h-5 mr-3" />
                  <div>
                    <div>{item.label}</div>
                    <div className="text-xs text-gray-500">{item.description}</div>
                  </div>
                </div>
              </Link>
            );
          })}
          
          {/* Probabilistic Section - only show for admin */}
          {probabilisticItems.length > 0 && (
            <>
              <div className="mt-4 mb-2 px-3">
                <div className={`text-xs font-semibold uppercase tracking-wider flex items-center ${darkMode ? 'text-fuchsia-300' : 'text-purple-400'}`}>
                  <Zap className="w-4 h-4 mr-2" />
                  Probabilistic
                </div>
              </div>

              {probabilisticItems.map((item) => {
                const Icon = item.icon;
                const isActive = isPathActive(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`block pl-8 pr-4 py-2 border-l-4 text-base font-medium transition-colors ${
                      isActive
                        ? (darkMode ? 'bg-slate-800 border-fuchsia-400 text-fuchsia-200' : 'bg-purple-50 border-purple-900 text-purple-900')
                        : (darkMode ? 'border-transparent text-slate-400 hover:bg-slate-800 hover:border-fuchsia-500 hover:text-fuchsia-200' : 'border-transparent text-gray-500 hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700')
                    }`}
                    onClick={() => {
                      document.getElementById('mobile-menu').classList.add('hidden');
                    }}
                  >
                    <div className="flex items-center">
                      <Icon className="w-5 h-5 mr-3" />
                      <div>
                        <div>{item.label}</div>
                        <div className="text-xs text-gray-500">{item.description}</div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </>
          )}

          {/* IDA1 Section - only show for admin */}
          {ida1Items.length > 0 && (
            <>
              <div className="mt-4 mb-2 px-3">
                <div className={`text-xs font-semibold uppercase tracking-wider flex items-center ${darkMode ? 'text-teal-300' : 'text-teal-400'}`}>
                  <GitCompare className="w-4 h-4 mr-2" />
                  IDA1
                </div>
              </div>

              {ida1Items.map((item) => {
                const Icon = item.icon;
                const isActive = isPathActive(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`block pl-8 pr-4 py-2 border-l-4 text-base font-medium transition-colors ${
                      isActive
                        ? (darkMode ? 'bg-slate-800 border-teal-400 text-teal-200' : 'bg-teal-50 border-teal-900 text-teal-900')
                        : (darkMode ? 'border-transparent text-slate-400 hover:bg-slate-800 hover:border-teal-500 hover:text-teal-200' : 'border-transparent text-gray-500 hover:bg-teal-50 hover:border-teal-300 hover:text-teal-700')
                    }`}
                    onClick={() => {
                      document.getElementById('mobile-menu').classList.add('hidden');
                    }}
                  >
                    <div className="flex items-center">
                      <Icon className="w-5 h-5 mr-3" />
                      <div>
                        <div>{item.label}</div>
                        <div className="text-xs text-gray-500">{item.description}</div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </>
          )}

          {/* Models Section - only show for admin */}
          {analysisItems.length > 0 && (
            <>
              <div className="mt-4 mb-2 px-3">
                <div className={`text-xs font-semibold uppercase tracking-wider flex items-center ${darkMode ? 'text-slate-400' : 'text-gray-400'}`}>
                  <Activity className="w-4 h-4 mr-2" />
                  Models
                </div>
              </div>

              {analysisItems.map((item) => {
                const Icon = item.icon;
                const isActive = isPathActive(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`block pl-8 pr-4 py-2 border-l-4 text-base font-medium transition-colors ${
                      isActive
                        ? (darkMode ? 'bg-slate-800 border-slate-200 text-slate-100' : 'bg-gray-50 border-gray-900 text-gray-900')
                        : (darkMode ? 'border-transparent text-slate-400 hover:bg-slate-800 hover:border-slate-500 hover:text-slate-200' : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700')
                    }`}
                    onClick={() => {
                      document.getElementById('mobile-menu').classList.add('hidden');
                    }}
                  >
                    <div className="flex items-center">
                      <Icon className="w-5 h-5 mr-3" />
                      <div>
                        <div>{item.label}</div>
                        <div className="text-xs text-gray-500">{item.description}</div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </>
          )}

          {/* Results Section - only show for admin */}
          {resultsItems.length > 0 && (
            <>
              <div className="mt-4 mb-2 px-3">
                <div className={`text-xs font-semibold uppercase tracking-wider flex items-center ${darkMode ? 'text-pink-300' : 'text-pink-400'}`}>
                  <UnicornIcon className="w-4 h-4 mr-2" />
                  Results
                </div>
              </div>

              {resultsItems.map((item) => {
                const Icon = item.icon;
                const isActive = isPathActive(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`block pl-8 pr-4 py-2 border-l-4 text-base font-medium transition-colors ${
                      isActive
                        ? (darkMode ? 'bg-slate-800 border-pink-400 text-pink-200' : 'bg-pink-50 border-pink-900 text-pink-900')
                        : (darkMode ? 'border-transparent text-slate-400 hover:bg-slate-800 hover:border-pink-500 hover:text-pink-200' : 'border-transparent text-gray-500 hover:bg-pink-50 hover:border-pink-300 hover:text-pink-700')
                    }`}
                    onClick={() => {
                      document.getElementById('mobile-menu').classList.add('hidden');
                    }}
                  >
                    <div className="flex items-center">
                      <Icon className="w-5 h-5 mr-3" />
                      <div>
                        <div>{item.label}</div>
                        <div className="text-xs text-gray-500">{item.description}</div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </>
          )}

          {/* User info and logout in mobile menu */}
          <div className={`mt-4 pt-4 pb-3 border-t ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
            <button
              type="button"
              onClick={onToggleDarkMode}
              className={`mx-4 mb-3 inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                darkMode
                  ? 'border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              Dark mode
            </button>
            <div className="flex items-center px-4 mb-3">
              <User className={`w-5 h-5 mr-2 ${darkMode ? 'text-slate-400' : 'text-gray-400'}`} />
              <span className={`text-base font-medium ${darkMode ? 'text-slate-100' : 'text-gray-800'}`}>{username}</span>
            </div>
            <button
              onClick={() => {
                document.getElementById('mobile-menu').classList.add('hidden');
                onLogout();
              }}
              className={`block w-full text-left px-4 py-2 text-base font-medium transition-colors ${
                darkMode ? 'text-slate-300 hover:text-slate-100 hover:bg-slate-800' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <LogOut className="w-5 h-5 mr-3 inline" />
              Logout
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
